/**
 * WebSocketContext — single shared WebSocket connection for all authenticated pages.
 *
 * Architecture:
 *   - ONE WebSocket connection per browser session (lives in Layout)
 *   - Components register/unregister channels and event handlers via context
 *   - Application-level ping every 20s keeps the Cloud LB alive (LB idle timeout = 30s)
 *   - Exponential backoff reconnection on disconnect
 *   - On reconnect all currently-registered channels are re-subscribed automatically
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAuth } from 'firebase/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WebSocketMessage {
  type: string;
  product?: string;
  channel?: string;
  service?: string;
  event?: string;
  data?: any;
  timestamp?: string;
}

/** Handler registered by a consumer for a specific (channel, event) pair. */
type EventHandler = (data: any) => void;

/** Map: channel → event → Set<handler> */
type HandlerMap = Map<string, Map<string, Set<EventHandler>>>;

interface WebSocketContextValue {
  /** Whether the WebSocket is currently open and confirmed connected. */
  connected: boolean;
  /** Register interest in a channel (sends subscribe if WS is open). */
  subscribeChannel: (channel: string) => void;
  /** Deregister interest. Channel is unsubscribed when ref count reaches 0. */
  unsubscribeChannel: (channel: string) => void;
  /** Register an event handler for a specific channel+event pair. */
  addEventHandler: (channel: string, event: string, handler: EventHandler) => void;
  /** Remove an event handler. */
  removeEventHandler: (channel: string, event: string, handler: EventHandler) => void;
  /** Whether auto-invalidate is enabled (can be read by consumers). */
  autoInvalidate: boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function useWebSocketContext(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocketContext must be used inside <WebSocketProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const PING_INTERVAL_MS = 20_000; // 20s — keeps Cloud LB alive (30s idle timeout)
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const AUTO_INVALIDATE = true;

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [connected, setConnected] = useState(false);

  // The live WebSocket instance
  const wsRef = useRef<WebSocket | null>(null);
  // Timer refs
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Reconnect state
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  // Channel reference counting: channel → subscriber count
  const channelCountRef = useRef<Map<string, number>>(new Map());
  // Event handlers: channel → event → Set<handler>
  const handlersRef = useRef<HandlerMap>(new Map());

  // Detect product from hostname (stable — doesn't change between renders)
  const product = (() => {
    const h = window.location.hostname;
    if (h.includes('assayer-ai.com') || h.includes('localhost')) return 'assayerai';
    if (h.includes('vireosentinel.com')) return 'vireosentinel';
    if (h.includes('salesplanpro.com')) return 'salesplanpro';
    if (h.includes('techtree-ai.com')) return 'techtree';
    return 'assayerai';
  })();

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  const sendSubscribe = useCallback((ws: WebSocket, channel: string) => {
    if (ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'subscribe', data: channel }));
  }, []);

  const sendPing = useCallback((ws: WebSocket) => {
    if (ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'ping' }));
  }, []);

  const startPingTimer = useCallback((ws: WebSocket) => {
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    pingTimerRef.current = setInterval(() => sendPing(ws), PING_INTERVAL_MS);
  }, [sendPing]);

  const stopPingTimer = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  // ------------------------------------------------------------------
  // Message handler
  // ------------------------------------------------------------------

  const handleMessage = useCallback((event: MessageEvent) => {
    let msg: WebSocketMessage;
    try {
      msg = JSON.parse(event.data);
    } catch {
      console.warn('[WebSocket] Failed to parse message:', event.data);
      return;
    }

    // Gateway control messages — log but don't process
    if (msg.type === 'connected' || msg.type === 'subscribed' || msg.type === 'pong') {
      console.log('[WebSocket] Control message:', msg.type);
      return;
    }

    const channel = msg.channel ?? '';
    const eventType = msg.event ?? '';

    console.log('[WebSocket] Received event:', { channel, event: eventType, data: msg.data });

    // Dispatch to registered handlers for this channel+event
    const channelHandlers = handlersRef.current.get(channel);
    if (channelHandlers && eventType) {
      const eventHandlers = channelHandlers.get(eventType);
      if (eventHandlers) {
        eventHandlers.forEach(handler => {
          try { handler(msg.data); } catch { /* handler errors must not break the loop */ }
        });
      }
    }

    // Auto-invalidate React Query cache based on channel
    if (AUTO_INVALIDATE && channel) {
      // Channel format: "assayerai:announcements" or "assayerai:tenant:X:announcement:Y"
      // Extract the primary resource type (second segment)
      const parts = channel.split(':');
      if (parts.length >= 2) {
        const resourceType = parts[1]; // "announcements", "tenant", etc.
        queryClient.invalidateQueries({ queryKey: [resourceType] });
        // Also invalidate specific announcement if present
        const annIdx = parts.indexOf('announcement');
        if (annIdx !== -1 && parts[annIdx + 1]) {
          queryClient.invalidateQueries({ queryKey: ['announcement', parts[annIdx + 1]] });
        }
      }
    }
  }, [queryClient]);

  // ------------------------------------------------------------------
  // Connect
  // ------------------------------------------------------------------

  const connect = useCallback(async () => {
    if (isConnectingRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    isConnectingRef.current = true;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        console.log('[WebSocket] No authenticated user, skipping connection');
        isConnectingRef.current = false;
        return;
      }

      const idToken = await user.getIdToken();
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host.replace(/^app\./, 'api.');
      const wsUrl = `${protocol}//${wsHost}/ws?token=${idToken}`;

      console.log('[WebSocket] Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WebSocket] Connected successfully');
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        setConnected(true);

        // Re-subscribe all currently registered channels
        const channelsToSubscribe = Array.from(channelCountRef.current.entries())
          .filter(([, count]) => count > 0)
          .map(([channel]) => channel);

        if (channelsToSubscribe.length > 0) {
          console.log('[WebSocket] Re-subscribing to channels:', channelsToSubscribe);
          channelsToSubscribe.forEach(channel => sendSubscribe(ws, channel));
        }

        // Start application-level ping to keep LB alive
        startPingTimer(ws);
      };

      ws.onmessage = handleMessage;

      ws.onerror = (error) => {
        console.error('[WebSocket] Connection error:', error);
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        console.log('[WebSocket] Connection closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        setConnected(false);
        isConnectingRef.current = false;
        stopPingTimer();

        // Exponential backoff reconnect
        const backoff = Math.min(
          RECONNECT_BASE_MS * Math.pow(2, reconnectAttemptsRef.current),
          RECONNECT_MAX_MS,
        );
        reconnectAttemptsRef.current++;

        console.log(`[WebSocket] Reconnecting in ${backoff}ms (attempt ${reconnectAttemptsRef.current})`);

        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(connect, backoff);
      };

    } catch (error) {
      console.error('[WebSocket] Connection attempt failed:', error);
      isConnectingRef.current = false;

      const backoff = Math.min(
        RECONNECT_BASE_MS * Math.pow(2, reconnectAttemptsRef.current),
        RECONNECT_MAX_MS,
      );
      reconnectAttemptsRef.current++;

      console.log(`[WebSocket] Reconnecting after error in ${backoff}ms (attempt ${reconnectAttemptsRef.current})`);

      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, backoff);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleMessage, sendSubscribe, startPingTimer, stopPingTimer]);

  // ------------------------------------------------------------------
  // Lifecycle — connect once on mount
  // ------------------------------------------------------------------

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      stopPingTimer();
      if (wsRef.current) {
        // Remove onclose so the cleanup doesn't trigger a reconnect
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [connect, stopPingTimer]);

  // ------------------------------------------------------------------
  // Public API — channel subscription
  // ------------------------------------------------------------------

  const subscribeChannel = useCallback((channel: string) => {
    const prev = channelCountRef.current.get(channel) ?? 0;
    channelCountRef.current.set(channel, prev + 1);

    if (prev === 0) {
      console.log('[WebSocket] Subscribing to channel:', channel);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendSubscribe(wsRef.current, channel);
      } else {
        console.log('[WebSocket] Connection not open yet, will subscribe when connected');
      }
    }
  }, [sendSubscribe]);

  const unsubscribeChannel = useCallback((channel: string) => {
    const prev = channelCountRef.current.get(channel) ?? 0;
    if (prev <= 1) {
      channelCountRef.current.delete(channel);
    } else {
      channelCountRef.current.set(channel, prev - 1);
    }
    // Note: we don't send an unsubscribe message to the gateway — channels are
    // lightweight server-side and will expire when the connection closes.
    // This avoids a round-trip and keeps the API simple.
  }, []);

  // ------------------------------------------------------------------
  // Public API — event handlers
  // ------------------------------------------------------------------

  const addEventHandler = useCallback((channel: string, event: string, handler: EventHandler) => {
    if (!handlersRef.current.has(channel)) {
      handlersRef.current.set(channel, new Map());
    }
    const channelMap = handlersRef.current.get(channel)!;
    if (!channelMap.has(event)) {
      channelMap.set(event, new Set());
    }
    channelMap.get(event)!.add(handler);
  }, []);

  const removeEventHandler = useCallback((channel: string, event: string, handler: EventHandler) => {
    handlersRef.current.get(channel)?.get(event)?.delete(handler);
  }, []);

  // ------------------------------------------------------------------
  // Context value
  // ------------------------------------------------------------------

  const value: WebSocketContextValue = {
    connected,
    subscribeChannel,
    unsubscribeChannel,
    addEventHandler,
    removeEventHandler,
    autoInvalidate: AUTO_INVALIDATE,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
